import * as React from 'react'

import { Box, Chevron, Popover, Text, useToggle } from '@factorialco/deprecated-design-system'
import type moment from 'moment'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'
import {
  formatCounterAmount,
  isHourlyFixedBalanceAllowance,
} from 'modules/timeoff/components/Leaves/Overview/formatCounterAmount'

import type Allowance from 'modules/timeoff/store/models/Allowance'
import type AllowanceIncidence from 'modules/timeoff/store/models/AllowanceIncidence'
import type { Cycle } from 'modules/timeoff/store/models/AllowanceStat'

import Accrued from './Accrued'
import Carryovers, { CarryOverData } from './Carryovers'
import Incidence from './Incidence'
import MaxBalanceCap from './MaxBalanceCap'
import Total from './Total'

interface Props {
  total: number
  accrued: number
  carryOvers: CarryOverData[]
  incidences: AllowanceIncidence[]
  allowance: Allowance
  accrualCycle: Cycle | null | undefined
  maxBalanceCap: number | null
  referenceDate: moment.Moment
}

const AvailableTooltip = ({
  total,
  accrued,
  carryOvers,
  incidences,
  allowance,
  accrualCycle,
  maxBalanceCap,
  referenceDate,
}: Props) => {
  const [open, toggle] = useToggle()

  if (total === null && !allowance.isTimeWorked) {
    return <Text.Header3>{i18n.t('time_off.unlimited')}</Text.Header3>
  }

  const hasReachMax =
    maxBalanceCap && Number(total) >= Number(maxBalanceCap) ? 'radicalRed1200' : 'grey1000'
  const isHourlyFixedBalance = isHourlyFixedBalanceAllowance(allowance)

  const title = allowance.isDays
    ? i18n.t('time_off.available_tooltip.available_days')
    : i18n.t('time_off.available_tooltip.available_hours')

  const formattedTotal = formatCounterAmount(total, isHourlyFixedBalance)
  const formattedAccrued = formatCounterAmount(accrued, isHourlyFixedBalance)
  const formattedMaxBalanceCap = formatCounterAmount(Number(maxBalanceCap), isHourlyFixedBalance)

  return (
    <Popover.ItemList
      attachment="top center"
      targetAttachment="bottom center"
      toggle={toggle}
      open={open}
      title={title}
    >
      <Box flexDirection="row" alignItems="center">
        <Box alignItems="center" gap="s4">
          <Box flexDirection="row" alignItems="center" gap="s4">
            <Text.Header1 color={hasReachMax}>{formattedTotal}</Text.Header1>
            <Chevron direction={open ? 'up' : 'down'} type="quaternary" />
          </Box>
          <Text.Header3>{i18n.t('time_off.available_stats')}</Text.Header3>
        </Box>
      </Box>
      <Accrued
        amount={formattedAccrued}
        accrualCycle={accrualCycle}
        accruedOnPrevCycle={allowance.isNextCycleAvailability}
      />
      {carryOvers.map((carryOver, index) => {
        return (
          <Carryovers
            key={index}
            allowance={allowance}
            carryOver={carryOver}
            isHourlyFixedBalance={isHourlyFixedBalance}
          />
        )
      })}
      {maxBalanceCap && <MaxBalanceCap amount={formattedMaxBalanceCap} />}
      {incidences.map((allowanceIncidence) => (
        <Incidence
          key={allowanceIncidence.attributes.id}
          allowanceIncidence={allowanceIncidence}
          isHourlyFixedBalance={isHourlyFixedBalance}
        />
      ))}
      <Total amount={formattedTotal} referenceDate={referenceDate} />
    </Popover.ItemList>
  )
}

export default observer(AvailableTooltip)
